import { createContext, useContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { auth, getDbSafe, getAuthSafe } from '../services/firebaseConfig';
import { bootMark } from '../debug/bootTracer';
import { onAuthStateChanged, signOut, User } from 'firebase/auth';
import { doc, onSnapshot, updateDoc, arrayUnion, arrayRemove, setDoc, getDoc, serverTimestamp, collection, writeBatch, query, where, getDocs, limit, addDoc, increment } from 'firebase/firestore';
import { User as AppUser, UserProfile, FriendRequest, ShoutedYap, Yap } from '../types';
import { toast } from 'sonner';
import { GoldService, SubscriptionService, GovernanceService, YapService, UsernameService, EconomyService, NotificationService } from '../services';
import { checkAndLiftExpiredMute } from '../services/WatcherService';
import { Forum, Tribe } from '../types';
import { DISABLE_ASYNC_EFFECTS } from '../debugConfig';
import { useSettings } from './SettingsContext';
import { SafeStorage } from '../utils/safeStorage';
import { recordLocalTimeProfile } from '../services/localTimeProfile';
import { MUTED_WORDS_CACHE_KEY, normalizeMutedWord, MAX_MUTED_WORDS } from '../utils/mutedWords';
import { getUserLocationText, getUserOriginFlag } from '../utils/userUtils';
import { calculateAge } from './utils/timeFormat';
import { useMetricDelta } from './MetricDeltaContext';
import { EVENTS } from '../constants/navigation';
import { triggerHaptic } from '../utils/haptic';

/**
 * A suspension, as the app understands it.
 *
 * `reason` is whatever the Watcher typed at ban time (banUser's third argument),
 * shown back to the user so a suspension screen explains itself rather than just
 * asserting itself.
 */
export interface BanState {
  banned: boolean;
  reason?: string;
  /** null/undefined = permanent, matching banUser's own convention. */
  expiresAt?: Date | null;
}

/**
 * Decide whether a user document represents a live ban.
 *
 * Deliberately a mirror of isBanned() in firestore.rules: both ban shapes
 * (`banned: true` from LegionService.banUser, and the older `status: 'banned'`
 * the rules used to read), and an expiry that genuinely lapses so a temporary
 * ban does not become permanent. Keep the two in step — the rules are the
 * enforcement, this is only what the user is told.
 */
export function resolveBanState(data: any): BanState {
  if (!data) return { banned: false };

  const toDate = (v: any): Date | null => {
    if (v == null) return null;
    if (typeof v?.toDate === 'function') return v.toDate();
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const statusBanned = data.status === 'banned';
  const flagBanned = data.banned === true;
  if (!statusBanned && !flagBanned) return { banned: false };

  const expiresAt = toDate(data.banExpiry);
  // A lapsed temporary ban is over. A status-based ban carries no expiry and so
  // is always permanent.
  if (flagBanned && !statusBanned && expiresAt && expiresAt.getTime() <= Date.now()) {
    return { banned: false };
  }

  return { banned: true, reason: data.banReason || undefined, expiresAt };
}

interface UserDataContextType {
  userProfile: UserProfile;
  firebaseUser: User | null;
  authLoading: boolean;
  /**
   * True once the profile Firestore snapshot has fired for the current
   * signed-in user (regardless of whether the doc exists). Use this in
   * route guards to avoid redirecting to /setup before the profile loads.
   */
  profileSettled: boolean;
  profileLoaded: boolean;
  /** Live suspension state for the signed-in user; drives the lockout screen. */
  banState: BanState;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  canChangeUsername: () => boolean;
  shoutedYaps: ShoutedYap[];
  addShout: (yap: Yap, caption?: string, images?: string[], audioData?: { url: string, duration: number }) => void;
  removeShout: (yapId: string) => Promise<void>;
  /** Prune a shout from local state only — for callers that already persisted the unshout. */
  removeShoutLocal: (yapId: string) => void;
  updateShoutCaption: (yapId: string, newCaption?: string, newImages?: string[]) => Promise<void>;
  isYapShouted: (yapId: string) => boolean;
  friends: AppUser[];
  addFriend: (friendId: string) => void;
  removeFriend: (friendId: string) => void;
  isFriend: (userId: string) => boolean;
  getFriendsCount: () => number;
  blockedUsers: string[];
  /**
   * Users who have blocked ME (mirrored server-side by onBlockedUsersChanged).
   * A client can't discover this on its own, so the trigger writes it here; the
   * UI hides the union of blockedUsers + blockedBy so a block works both ways.
   */
  blockedBy: string[];
  blockUser: (userId: string) => void;
  unblockUser: (userId: string) => void;
  isUserBlocked: (userId: string) => boolean;
  /**
   * Words/phrases this user has muted. Stored privately at
   * users/{uid}/settings/preferences — NOT on the profile document, which any
   * signed-in user can read. Consumed via useContentFilter, not directly.
   */
  mutedWords: string[];
  muteWord: (word: string) => Promise<void>;
  unmuteWord: (word: string) => Promise<void>;
  friendRequests: FriendRequest[];
  pendingFriendRequests: FriendRequest[];
  sendFriendRequest: (toUserId: string, toUserName: string, toUserUsername: string, toUserAvatar: string, toUserAge?: number) => Promise<{ isMatch: boolean; userData?: any }>;
  passUser: (targetUserId: string) => Promise<void>;
  acceptFriendRequest: (requestId: string) => void;
  rejectFriendRequest: (requestId: string) => void;
  cancelFriendRequest: (toUserId: string, refundAmount?: number) => Promise<void>;
  hasPendingRequestTo: (userId: string) => boolean;
  /** False until the outgoing-request listener has delivered a snapshot. */
  outgoingRequestsLoaded: boolean;
  hasPendingRequestFrom: (userId: string) => boolean;
  acceptGroupInvite: (entityType: 'forum' | 'tribe', entityId: string) => Promise<void>;
  joinTribe: (tribeId: string) => Promise<void>;
  leaveTribe: (tribeId: string) => Promise<void>;
  isTribeMember: (tribeId: string, ownerId?: string) => boolean;
  joinForum: (forumId: string) => Promise<void>;
  leaveForum: (forumId: string) => Promise<void>;
  isForumMember: (forumId: string, ownerId?: string) => boolean;
  canPostAnonymously: () => boolean;
  getAnonymousPostsRemaining: () => number;
  getTimeUntilNextAnonymousPost: () => string | null;
  // Economy & Governance
  claimDailyStreak: () => Promise<void>;
  rewardAd: () => Promise<void>;
  purchaseGoldPack: (packId: string, amount: number, price: string) => Promise<boolean>;
  createForum: (data: Partial<Forum>) => Promise<string>;
  createTribe: (data: Partial<Tribe>, paymentMethod?: 'credit' | 'gold') => Promise<string>;
  passOwnership: (type: 'forum' | 'tribe', id: string, newOwnerId: string) => Promise<void>;
  refreshEntitlements: () => Promise<void>;
  simulateSubscription: (tier: 'yapper' | 'elite' | 'none') => Promise<void>;
  passedUserIds: Set<string>;
  likedUserIds: string[];
  sentRequestIds: string[];
}

const defaultUserProfile: UserProfile = {
  name: '',
  username: '',
  location: '',
  education: '',
  occupation: '',
  bio: '',
  avatar: '',
  bannerImage: '',
  coverImage: '',
  countryFlag: '',
  country: '',
  age: 0,
  identities: [],
  portfolio: [],
  profilePictureIndex: 0,
  lastUsernameChange: undefined,
  friends: [],
  dmPrivacy: 'friends',
  profilePrivacy: 'public',
  showLocation: true,
  showInDiscover: true,
  showFriendList: true,
  showTribesList: true,
  tribes: [],
  portfolioPreviews: [],
  likedYapIds: [],
  likedShoutIds: [],
  shoutedYapIds: [],
  rank: 'user', // 🔒 FIX: Explicit default prevents permission checks failing before snapshot arrives
};

const UserDataContext = createContext<UserDataContextType | undefined>(undefined);

export function UserDataProvider({ children }: { children: ReactNode }) {
  const [firebaseUser, setFirebaseUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile>(defaultUserProfile);
  const { adjustMetric, resetMetric, getOptimisticCount } = useMetricDelta();
  const [authLoading, setAuthLoading] = useState(true);
  const [profileSettled, setProfileSettled] = useState(false);
  // True only once we've actually read the user's profile document. Distinguishes
  // a genuinely incomplete account (send to setup) from a profile we couldn't load
  // because of a bad connection (show offline screen instead of setup/logout).
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false); // Lock for concurrent updates
  const { syncFromProfile } = useSettings();

  // Initialize with mock shouted yaps for user ID 7 (Amara)
  const [shoutedYaps, setShoutedYaps] = useState<ShoutedYap[]>([]); // Changed to empty array, will be fetched
  const [friends, setFriends] = useState<AppUser[]>([]);
  const [blockedUsers, setBlockedUsers] = useState<string[]>([]);
  const [blockedBy, setBlockedBy] = useState<string[]>([]);
  const [banState, setBanState] = useState<BanState>({ banned: false });
  // Seeded synchronously from the local mirror so the very first feed paint
  // already knows the mute list. Waiting for the Firestore listener would let
  // muted content flash on screen before being hidden.
  const [mutedWords, setMutedWords] = useState<string[]>(() => {
    try {
      const cached = SafeStorage.getItem(MUTED_WORDS_CACHE_KEY);
      const parsed = cached ? JSON.parse(cached) : null;
      return Array.isArray(parsed) ? parsed.filter((w: any) => typeof w === 'string') : [];
    } catch {
      return [];
    }
  });
  const [friendRequests, setFriendRequests] = useState<FriendRequest[]>([]);
  const [outgoingRequests, setOutgoingRequests] = useState<string[]>([]);
  /**
   * Has the outgoing-requests listener delivered its first snapshot?
   *
   * Without this, consumers cannot tell "you have no pending requests" from
   * "we have not looked yet", because both are an empty array — so a profile
   * with a pending request painted "Connect" and snapped to "Cancel Request"
   * when the snapshot landed.
   */
  const [outgoingRequestsLoaded, setOutgoingRequestsLoaded] = useState(false);
  const [optimisticFriends, setOptimisticFriends] = useState<string[]>([]);
  const [incomingRequests, setIncomingRequests] = useState<Map<string, { name: string; username: string; avatar: string; age: string; location: string }>>(new Map());
  const [passedUserIds, setPassedUserIds] = useState<Set<string>>(new Set());
  const pendingRequestsRef = useRef<Set<string>>(new Set());

  // 🔒 PATCH: Initialize passedUserIds from LocalStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('passedUserIds');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setPassedUserIds(prev => new Set([...prev, ...parsed]));
        }
      }
    } catch (e) {
      console.warn('Failed to load passedUserIds from localStorage', e);
    }
  }, []);

  const [likedUserIds, setLikedUserIds] = useState<Set<string>>(new Set());

  // 🔒 PATCH: Initialize likedUserIds from LocalStorage on mount
  useEffect(() => {
    try {
      const stored = localStorage.getItem('likedUserIds');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed)) {
          setLikedUserIds(prev => new Set([...prev, ...parsed]));
        }
      }
    } catch (e) {
      console.warn('Failed to load likedUserIds from localStorage', e);
    }
  }, []);

  // Import definition is below

  useEffect(() => {
    bootMark('UserDataProviderMount');
    // ASYNC GUARD
    if (DISABLE_ASYNC_EFFECTS) {
      console.log("ASYNC EFFECT SKIPPED: UserData.AuthState");
      window.dispatchEvent(new CustomEvent('SKIPPED_EFFECT', { detail: 'UserData.AuthState' }));
      return;
    }

    let unsubscribeAuth: (() => void) | null = null;
    let cancelled = false;

    const setupAuth = async () => {
      try {
        bootMark('UserDataInitStart');
        const { getAuthSafe, getDbSafe } = await import('../services/firebaseConfig');
        const authInstance = await getAuthSafe();
        const dbInstance = await getDbSafe();

        if (cancelled) return;

        unsubscribeAuth = onAuthStateChanged(authInstance, (user) => {
          if (cancelled) return;
          setFirebaseUser(user);

          if (user) {
            console.log(`[UserDataContext] Auth resolved for user: ${user.uid}`);
            // Remember that a real session exists so a later connectivity failure
            // (auth can't resolve) shows the offline screen instead of "logging out".
            try { localStorage.setItem('afroyap_had_session', '1'); } catch (_) {}

            // Is this account still real?
            //
            // Firebase hands back a cached user from persistence without asking
            // the server, so an account deleted anywhere else — another device,
            // the console, an admin purge — keeps working here until its token
            // happens to need refreshing, which can be an hour away. That is how
            // a deleted account stays browsable, and then surfaces as a mystery
            // logout later on.
            //
            // Forcing a token refresh asks the server directly. Only the codes
            // that positively mean "this account is gone" sign the session out;
            // a network failure must fall through untouched, or every offline
            // launch would log the user out — the exact hostility the session
            // marker exists to prevent, and one this app cannot afford, since
            // almost every account signs in against a synthetic
            // @temp.afroyap.com address with no recovery path.
            user.getIdToken(true).catch((err: any) => {
              const code = String(err?.code || '');
              const accountIsGone =
                code === 'auth/user-not-found' ||
                code === 'auth/user-disabled' ||
                code === 'auth/user-token-expired' ||
                code === 'auth/invalid-user-token';
              if (!accountIsGone) return;

              console.warn(`[UserDataContext] Account no longer exists (${code}) — signing out.`);
              // Clear the marker FIRST: while it is set, ProtectedRoute reads a
              // missing user as a connectivity fault and shows the reconnect
              // screen, which is exactly the wrong destination for an account
              // that will never come back.
              try { localStorage.removeItem('afroyap_had_session'); } catch (_) {}
              signOut(authInstance).catch(() => { /* the auth state listener still fires below */ });
            });
            // Auto-lift expired mutes on login
            checkAndLiftExpiredMute(user.uid).catch(console.error);

            // 🛡️ Set ID immediately so owner checks (isForumMember) work even before snapshot returns
            // Note: This will be overwritten by the real numeric Profile ID if it exists in the document.
            setUserProfile(prev => ({ ...prev, id: prev.id || user.uid }));
            
            // 🛡️ SECURITY: Clear any stale login intent immediately upon successful auth
            // This prevents background audits from mistakenly using old metadata
            import('../utils/safeStorage').then(({ SafeStorage }) => {
              SafeStorage.removeItem('login_intent_username');
            }).catch(() => {});
            
            bootMark('UserData:SetupSnapshot');
            // 🔒 REAL-TIME SYNC: Listen for profile changes
            const userRef = doc(dbInstance, 'users', user.uid);
            // Whether this listener has seen a copy of the document that came from
            // the server (or a cached copy of one). Until it has, a snapshot that
            // exists only because of this device's own pending write is NOT the
            // profile.
            //
            // The cache is memory-only, so on a cold launch it is empty. Any
            // setDoc(..., { merge: true }) on users/{uid} issued before the server
            // answers (recordLocalTimeProfile did exactly that on the first
            // heartbeat) makes Firestore raise a snapshot that "exists" holding
            // only the merged fields: no username, no setupComplete, no photos.
            // Read as the profile, that failed the setup check and ProtectedRoute
            // sent a fully onboarded user to step 1 of setup. Its "Back to Login"
            // button then signed out accounts whose owners have no password to
            // sign back in with. On a good network the server copy wins the race;
            // on a shaky one the partial copy can stand for many seconds.
            let serverCopySeen = false;
            const unsubscribeProfile = onSnapshot(userRef, (docSnap) => {
              if (cancelled) return;
              const { fromCache, hasPendingWrites } = docSnap.metadata;
              if (!fromCache || !hasPendingWrites) serverCopySeen = true;
              if (!serverCopySeen) {
                // Only our own unconfirmed write: neither a profile nor proof that
                // one is missing. Keep waiting for the server's answer.
                console.warn('[UserDataContext] Ignoring local-only profile snapshot (pending write, no server copy yet).');
                return;
              }
              if (docSnap.exists()) {
                const data = docSnap.data();
                setBlockedUsers(data.blockedUsers || []);
                setBlockedBy(data.blockedBy || []);

                // 🚫 SUSPENSION: derived on every snapshot, not once at boot, so a
                // Watcher banning someone mid-session locks them out on the next
                // render rather than at their next cold start.
                //
                // Mirrors isBanned() in firestore.rules exactly — both ban shapes,
                // and an expiry that actually lapses. If these two ever disagree
                // again the app either shows a lockout the server won't enforce,
                // or lets someone use an app whose every write is denied; the
                // rules remain the authority, this is the explanation.
                setBanState(resolveBanState(data));

                // 🔒 HEALING: Ensure showLocation defaults to true for all users.
                // Fix: Reset showLocation to true if it's falsy (undefined, null, or false)
                // UNLESS the user has explicitly set it AFTER our recent fix rollout.
                const hasExplicitlySetLocation = data.showLocationSetAt != null && new Date(data.showLocationSetAt) > new Date('2026-03-30T00:00:00Z');
                if ((data.showLocation === undefined || data.showLocation === null || data.showLocation === false) && !hasExplicitlySetLocation && user.uid) {
                  console.log(`[UserDataContext] Healing showLocation to true for user: ${user.uid} (was: ${data.showLocation})`);
                  const docRef = doc(dbInstance, 'users', user.uid);
                  updateDoc(docRef, { showLocation: true }).catch(err => {
                    console.error('[UserDataContext] Healing failed:', err);
                  });
                }

                // 🔒 HEALING: Ensure showFriendList and showTribesList default to true for all users.
                // This maximizes community engagement by default.
                const hasExplicitlySetFriendList = data.showFriendListSetAt != null;
                const hasExplicitlySetTribeList = data.showTribesListSetAt != null;
                const socialHealing: any = {};
                
                if (data.showFriendList === undefined && !hasExplicitlySetFriendList) {
                  socialHealing.showFriendList = true;
                }
                if (data.showTribesList === undefined && !hasExplicitlySetTribeList) {
                  socialHealing.showTribesList = true;
                }
                if (data.showYaps === undefined && data.showYapsSetAt == null) {
                  socialHealing.showYaps = true;
                }
                
                if (Object.keys(socialHealing).length > 0 && user.uid) {
                  const docRef = doc(dbInstance, 'users', user.uid);
                  updateDoc(docRef, socialHealing).catch(err => {
                    console.error('[UserDataContext] Social healing failed:', err);
                  });
                }

                // 🔒 HEALING: Consolidate Malinke/Mandinka
                if (data.identities && (data.identities.includes('Malinke') || data.identities.includes('Mandinka') || data.identities.includes('Malinke/Mandinka'))) {
                  const newIdentities = data.identities.map((id: string) => 
                    (id === 'Malinke' || id === 'Mandinka' || id === 'Malinke/Mandinka') ? 'Mandinka/Malinke' : id
                  );
                  // Deduplicate
                  const uniqueIdentities = Array.from(new Set(newIdentities));
                  if (JSON.stringify(uniqueIdentities) !== JSON.stringify(data.identities)) {
                    console.log(`[UserDataContext] Healing identities for user: ${user.uid}`);
                    const userRef = doc(dbInstance, 'users', user.uid);
                    updateDoc(userRef, { identities: uniqueIdentities }).catch(err => {
                      console.error('[UserDataContext] Identity healing failed:', err);
                    });
                  }
                }

                // 🔒 HEALING: Hardcode 'draymond' user to watcher rank specifically for this evaluation phase
                if (data.username === 'draymond' && data.rank !== 'watcher') {
                  const docRef = doc(dbInstance, 'users', user.uid);
                  updateDoc(docRef, { rank: 'watcher' }).catch(err => {
                    console.error('[UserDataContext] Failed to promote draymond:', err);
                  });
                }

                // 🔒 HEALING: Clear any dev-simulated subscription for 'draymond'.
                // simulateSubscription() was called during testing, writing elite tier to Firestore.
                // This resets it in Firestore AND immediately in local state so the UI is correct.
                let subscriptionOverrides: Record<string, any> = {};
                if (data.username === 'draymond' && (data.subscriptionActive === true || (data.subscriptionTier && data.subscriptionTier !== 'none'))) {
                  const docRef = doc(dbInstance, 'users', user.uid);
                  updateDoc(docRef, { subscriptionActive: false, subscriptionTier: 'none' }).catch(err => {
                    console.error('[UserDataContext] Failed to clear draymond subscription:', err);
                  });
                  // Apply immediately to local state so UI doesn't show stale elite values
                  subscriptionOverrides = { subscriptionActive: false, subscriptionTier: 'none' };
                }

                setUserProfile(prev => ({ 
                  ...prev, 
                  ...data, 
                  ...subscriptionOverrides,
                  id: data.id || user.uid,
                  showLocation: data.showLocation ?? true, // Explicit fallback
                  blockedUsers: data.blockedUsers || [],
                  joinedForumIds: data.joinedForumIds || [],
                  joinedTribeIds: data.joinedTribeIds || [],
                  ownedForumIds: data.ownedForumIds || [],
                  ownedTribeIds: data.ownedTribeIds || [],
                  showYaps: data.showYaps ?? true,
                } as UserProfile));
                
                // 🔒 CLEANUP OPTIMISM: When a real-time update arrives from Firestore,
                // reset any local session deltas for this user to prevent "double counting".
                resetMetric(user.uid, 'user', 'friendsCount');
                resetMetric(user.uid, 'user', 'slapCount');

                // 🔒 PRIVACY SYNC: Push Firestore privacy settings into SettingsContext
                // so they are authoritative across devices (not just from localStorage).
                syncFromProfile({
                  showFriendList: data.showFriendList ?? true,
                  friendListVisibility: data.friendListVisibility || 'everyone',
                  showTribesList: data.showTribesList ?? true,
                  tribeListVisibility: data.tribeListVisibility || 'everyone',
                  showYaps: data.showYaps ?? true,
                  showInDiscover: data.showInDiscover ?? true,
                  // Absent means friends-only, matching how other viewers read it.
                  allowMessages: data.allowMessages || 'friends',
                });
                // Real profile data is in hand — safe to make setup-completeness
                // decisions off it.
                setProfileLoaded(true);
              } else {
                console.warn('[UserDataContext] Profile document does not exist for UID:', user.uid);
              }

              setProfileSettled(true);
              if (authLoading) {
                setAuthLoading(false);
                bootMark('UserDataInitDone');
              }
            }, (err) => {
              // Snapshot failed (typically offline / network). Mark settled so the
              // app stops waiting, but leave profileLoaded false so ProtectedRoute
              // shows the offline screen rather than bouncing to setup/login.
              console.error("UserDataProvider: Snapshot error", err);
              setProfileLoaded(false);
              setProfileSettled(true);
              if (authLoading) setAuthLoading(false);
            });

            // 🔇 MUTED WORDS: private preferences live in a subcollection, not on
            // the user document — `allow get` on users/{uid} is open to any signed-in
            // user, and a mute list (slurs aimed at you, a person's name, a health
            // topic) must not be readable by anyone else. Costs one extra listener;
            // the words themselves then arrive free of any further reads.
            const prefsRef = doc(dbInstance, 'users', user.uid, 'settings', 'preferences');
            const unsubscribePrefs = onSnapshot(prefsRef, (prefsSnap) => {
              if (cancelled) return;
              const raw = prefsSnap.exists() ? (prefsSnap.data() as any).mutedWords : null;
              const words = Array.isArray(raw) ? raw.filter((w: any) => typeof w === 'string') : [];
              setMutedWords(words);
              SafeStorage.setItem(MUTED_WORDS_CACHE_KEY, JSON.stringify(words));
            }, (err) => {
              // Never fatal: failing to load the mute list must not break the app.
              // The locally mirrored list stays in effect.
              console.error('[UserDataContext] Muted words snapshot error:', err);
            });

            // Add profile unsubscription to clean up
            const originalUnsubscribeAuth = unsubscribeAuth;
            unsubscribeAuth = () => {
              if (originalUnsubscribeAuth) originalUnsubscribeAuth();
              unsubscribeProfile();
              unsubscribePrefs();
            };
          } else {
            // No user, done loading
            // Only treat this as a genuine logout/kick when we're actually online.
            // A null auth state while offline must NOT clear the session marker,
            // so ProtectedRoute can show the offline screen instead of login.
            if (typeof navigator === 'undefined' || navigator.onLine) {
              try { localStorage.removeItem('afroyap_had_session'); } catch (_) {}
            }
            // 🔒 SECURITY: Force clear all user state on logout/kick
            setFirebaseUser(null);
            setUserProfile(defaultUserProfile);
            setProfileSettled(false);
            // A signed-out session has no suspension; leaving this set would show the
            // lockout screen to whoever signs in next on this device.
            setBanState({ banned: false });
            setProfileLoaded(false);
            setFriends([]);
            setBlockedUsers([]);
            setShoutedYaps([]);
            setFriendRequests([]);
            setOutgoingRequests([]);
            setOutgoingRequestsLoaded(false);
            setIncomingRequests(new Map());
            setPassedUserIds(new Set());
            setLikedUserIds(new Set());
            
            if (!cancelled) {
              setAuthLoading(false);
              bootMark('UserDataInitDone');
            }
          }
        });
      } catch (err) {
        console.error("UserDataProvider: Auth init failed", err);
        if (!cancelled) setAuthLoading(false);
      }
    };

    setupAuth();

    // Fallback timeout for auth hanging (e.g. missing config)
    // 🔒 iOS FIX (Bug #3): On auth timeout, do NOT inject a mock user.
    // A fake user causes silent Firestore write failures (wrong UID) and
    // a confusing "logout" when real auth eventually resolves. Instead, we
    // simply mark loading as done with no user — App.tsx routes to login.
    const authTimeout = setTimeout(() => {
      setAuthLoading((loading) => {
        if (loading) {
          console.warn('[UserDataContext] Auth timed out. Resolving with no user — app will route to login.');
          return false;
        }
        return loading;
      });
    }, 15000);

    return () => {
      cancelled = true;
      if (unsubscribeAuth) unsubscribeAuth();
      clearTimeout(authTimeout);
    };
  }, []);

  // Presence System
  useEffect(() => {
    // ASYNC GUARD
    if (DISABLE_ASYNC_EFFECTS) {
      console.log("ASYNC EFFECT SKIPPED: UserData.Presence");
      window.dispatchEvent(new CustomEvent('SKIPPED_EFFECT', { detail: 'UserData.Presence' }));
      return;
    }

    if (!firebaseUser) return;

    // The writes live in PresenceService so this loop and the activity-status
    // surfaces agree on the document. Loading the viewer's own "Show my activity
    // status" setting here too, once per sign-in, is what the reciprocal hide
    // reads on the client.
    const uid = firebaseUser.uid;
    let presence: typeof import('../services/PresenceService').PresenceService | null = null;
    const presenceReady = import('../services/PresenceService').then(m => {
      presence = m.PresenceService;
      void presence.loadMyVisibility(uid);
    });

    const setOnline = async () => {
      await presenceReady;
      await presence?.heartbeat(uid);
      // Rides the heartbeat rather than adding its own timer, and writes at
      // most once per local day. YapStats needs to know when this user is
      // actually around; nothing else records that.
      void recordLocalTimeProfile(uid);
    };

    const setOffline = async () => {
      await presenceReady;
      await presence?.markOffline(uid);
    };

    setOnline();

    // Heartbeat: Update last_seen every 60 seconds to prevent ghosting
    // and recover from multi-tab "offline" overwrites
    const heartbeatInterval = setInterval(() => {
      if (document.visibilityState === 'visible') {
        setOnline();
      }
    }, 60000);

    // Handle tab close / browser close
    const handleBeforeUnload = () => {
      setOffline();
    };

    // Handle visibility change (tab switch)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        setOnline();
      } else {
        // Optional: Set away/offline on background? 
        // For now, let's keep them online as long as the tab is open, 
        // relying on heartbeat to keep it fresh.
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(heartbeatInterval);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      setOffline();
      // Nothing cached for this account should carry into the next sign-in.
      void presenceReady.then(() => presence?.reset());
    };
  }, [firebaseUser]);

  // Sync friends from subcollection
  useEffect(() => {
    // ASYNC GUARD
    if (DISABLE_ASYNC_EFFECTS) {
      console.log("ASYNC EFFECT SKIPPED: UserData.FriendSync");
      window.dispatchEvent(new CustomEvent('SKIPPED_EFFECT', { detail: 'UserData.FriendSync' }));
      return;
    }

    if (!firebaseUser) {
      setFriends([]);
      return;
    }

    let unsubscribe: (() => void) | undefined;
    const setupSync = async () => {
      const db = await getDbSafe();
      const friendsRef = collection(db, 'users', firebaseUser.uid, 'friends');
      unsubscribe = onSnapshot(friendsRef, async (snapshot) => {
        // Keep the friendship createdAt (added-at time) alongside each id so the
        // friends list can be ordered by newest add.
        const friendEntries = snapshot.docs.map(d => ({ id: d.id, friendedAt: d.data()?.createdAt ?? d.data()?.addedAt ?? null }));
        const friendIds = friendEntries.map(e => e.id);

        if (friendIds.length === 0) {
          setFriends([]);
          return;
        }

        // Fetch user details for friends
        // Note: In a real app, we might want to cache these or use a separate listener for each friend if we need real-time updates of their profile
        // For now, fetching on list change is sufficient
        // 🔒 PATCH 8D: Defensive friend sync with ghost reference handling
        try {
          const friendsData = await Promise.all(friendEntries.map(async ({ id: fid, friendedAt }) => {
            // Validate friend ID
            if (!fid || typeof fid !== 'string') {
              console.warn('[UserDataContext] Invalid friend ID:', fid);
              return null;
            }

            try {
              const userDoc = await getDoc(doc(db, 'users', fid));
              if (userDoc.exists()) {
                const data = userDoc.data();
                // Validate critical fields exist
                if (!data.name || !data.username) {
                  console.warn('[UserDataContext] Friend document missing critical fields:', fid);
                  return null;
                }
                return { id: userDoc.id, ...data, friendedAt } as unknown as AppUser;
              } else {
                console.warn('[UserDataContext] Ghost friend reference (user deleted):', fid);
                return null;
              }
            } catch (fetchError) {
              console.error('[UserDataContext] Error fetching friend:', fid, fetchError);
              return null;
            }
          }));

          // Filter out null entries (ghost friends, corrupted docs, fetch failures)
          const validFriends = friendsData.filter(f => f !== null) as AppUser[];
          setFriends(validFriends);
        } catch (error) {
          console.error("[CRITICAL] Error in friend sync:", error);
          // Keep existing friends list on error
        }
      });
    };
    setupSync();

    // 🔒 Social Graph Authority: Listen for real friend requests
    let unsubscribeRequests: (() => void) | null = null;
    let unsubscribeOutgoing: (() => void) | null = null;
    let unsubscribeFriends: (() => void) | null = null;

    const setupRequestSync = async () => {
      const db = await getDbSafe();
      // 🔒 FIX: Firestore does NOT allow two 'in' operators in one query.
      // Use '==' for userId (always a string UID) and 'in' for type.
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', String(firebaseUser.uid)),
        where('type', 'in', ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'])
        // 🔒 FIX: Don't filter by read status, otherwise clicking the notification removes the request!
        // where('read', '==', false) 
      );

      unsubscribeRequests = onSnapshot(q, (snapshot) => {
        const requests = snapshot.docs
          .map(doc => {
            const data = doc.data();
            // fromUserId may be in metadata OR at top-level senderId for legacy docs
            const fromUserId = String(
              data.metadata?.fromUserId ||
              data.senderId ||
              data.fromUserId ||
              ''
            );
            return {
              id: doc.id,
              fromUserId,
              fromUserName: data.userName || data.metadata?.fromUserName,
              fromUserUsername: data.userUsername || data.metadata?.fromUsername,
              fromUserAvatar: data.userAvatar || data.metadata?.fromUserAvatar,
              timestamp: data.timestamp?.toDate ? data.timestamp.toDate().toISOString() : new Date().toISOString(),
              status: data.status || 'pending' // Default to pending if missing
            } as FriendRequest;
          })
          // Filter out handled requests (accepted/rejected)
          .filter(req => req.status !== 'accepted' && req.status !== 'rejected');
        
        console.log(`[SocialGraph] Incoming friend requests synced: ${requests.length}`, requests.map(r => r.fromUserId));
        setFriendRequests(requests);
      }, (err) => {
        console.error('[SocialGraph] friendRequests listener error:', err);
      });
    };

    const setupOutgoingSync = async () => {
      const db = await getDbSafe();
      // Listen for friend_request notifications WE sent TO others
      // Use single-field query to avoid composite index requirement
      const userIds = [firebaseUser.uid, Number(firebaseUser.uid)].filter(v => typeof v !== 'number' || !isNaN(v as any));
      const q = query(
        collection(db, 'notifications'),
        where('metadata.fromUserId', 'in', userIds)
      );

      unsubscribeOutgoing = onSnapshot(q, (snapshot) => {
        const targetIds = snapshot.docs
          .filter(doc => {
            const data = doc.data();
            // Only count friend_requests that are still pending
            return (data.type === 'FRIEND_REQUEST' || data.type === 'friend_request' || data.type === 'CONNECTION_REQUEST' || data.type === 'connection_request') && 
                   data.status !== 'accepted' && 
                   data.status !== 'rejected';
          })
          .map(doc => String(doc.data().userId));
        console.log(`[SocialGraph] Outgoing requests synced: ${targetIds.length} pending`, targetIds);
        setOutgoingRequests(targetIds);
        setOutgoingRequestsLoaded(true);
      }, (error) => {
        console.error('[SocialGraph] Outgoing requests listener error:', error);
      });
    };

    const setupFriendsSync = async () => {
      const db = await getDbSafe();
      // 🔒 Social Graph Authority 4: Real-time Friends Sync
      const friendsRef = collection(db, 'users', String(firebaseUser.uid), 'friends');
      unsubscribeFriends = onSnapshot(friendsRef, async (snapshot) => {
        const friendDocs = await Promise.all(snapshot.docs.map(async (fDoc) => {
          const fId = String(fDoc.id);
          // Friendship createdAt (added-at time) — used to order by newest add.
          const friendedAt = fDoc.data()?.createdAt ?? fDoc.data()?.addedAt ?? null;
          // Fetch basic profile info for the friend
          try {
            const fProfileSnap = await getDoc(doc(db, 'users', fId));
            if (fProfileSnap.exists()) {
              const fData = fProfileSnap.data();
              return {
                id: fId,
                name: fData.name || fData.displayName || 'User',
                username: fData.username || '',
                image: fData.avatar || fData.image || '',
                avatar: fData.avatar || '',
                country: fData.country || '',
                countryFlag: fData.countryFlag || '',
                rank: fData.rank || 'user',
                friendedAt,
              };
            }
          } catch (e) {
            console.warn(`[SocialGraph] Failed to fetch profile for friend ${fId}:`, e);
          }
          return { id: fId, name: 'User', username: '', avatar: '', image: '' };
        }));
        
        console.log(`[SocialGraph] Real-time Friends synced: ${friendDocs.length} friends`);
        setFriends(friendDocs.filter(f => f.id) as any[]);
        // Clear optimistic friends that have been confirmed by the listener
        setOptimisticFriends(prev => prev.filter(id => !friendDocs.some(f => String(f.id) === String(id))));
      });
    };

    setupRequestSync();
    setupOutgoingSync();
    setupFriendsSync();

    return () => {
      if (unsubscribe) unsubscribe();
      if (unsubscribeRequests) unsubscribeRequests();
      if (unsubscribeOutgoing) unsubscribeOutgoing();
      if (unsubscribeFriends) unsubscribeFriends();
    };
  }, [firebaseUser]);

  // Fetch shouted yaps
  useEffect(() => {
    // ASYNC GUARD
    if (DISABLE_ASYNC_EFFECTS) {
      console.log("ASYNC EFFECT SKIPPED: UserData.Shouts");
      window.dispatchEvent(new CustomEvent('SKIPPED_EFFECT', { detail: 'UserData.Shouts' }));
      return;
    }

    const fetchShouts = async () => {
      if (userProfile.id) {
        const shouts = await YapService.fetchUserShoutedYaps(String(userProfile.id));
        setShoutedYaps(shouts);
      }
    };
    fetchShouts();

    window.addEventListener('yapUpdated', fetchShouts);
    return () => {
      window.removeEventListener('yapUpdated', fetchShouts);
    };
  }, [userProfile.id]);

  // 🛡️ REAL-TIME OWNERSHIP SYNC: Ensure owned group counts are "truly accurate" by listening to the collections
  useEffect(() => {
    if (DISABLE_ASYNC_EFFECTS || !firebaseUser || !userProfile.id) return;

    let unsubTribes: (() => void) | undefined;
    let unsubForums: (() => void) | undefined;

    const setupOwnershipListeners = async () => {
      try {
        const db = await getDbSafe();
        
        // 1. Listen for Owned Tribes
        const tribesRef = collection(db, 'tribes');
        const tribesQuery = query(tribesRef, where('ownerId', '==', firebaseUser.uid));
        unsubTribes = onSnapshot(tribesQuery, (snap) => {
          const actualIds = snap.docs.map(d => d.id);
          
          setUserProfile(prev => {
            const currentIds = prev.ownedTribeIds || [];
            const isSame = JSON.stringify([...actualIds].sort()) === JSON.stringify([...currentIds].sort());
            
            if (!isSame) {
              console.log(`[UserDataContext] Ownership Sync: Updating tribe IDs for ${firebaseUser.uid}`, actualIds);
              
              // Also heal the document if it's out of sync
              const userRef = doc(db, 'users', firebaseUser.uid);
              updateDoc(userRef, { ownedTribeIds: actualIds }).catch(e => console.warn('Failed to heal ownedTribeIds doc:', e));
              
              return { ...prev, ownedTribeIds: actualIds };
            }
            return prev;
          });
        });

        // 2. Listen for Owned Forums
        const forumsRef = collection(db, 'forums');
        const forumsQuery = query(forumsRef, where('ownerId', '==', firebaseUser.uid));
        unsubForums = onSnapshot(forumsQuery, (snap) => {
          const actualIds = snap.docs.map(d => d.id);

          setUserProfile(prev => {
            const currentIds = prev.ownedForumIds || [];
            const isSame = JSON.stringify([...actualIds].sort()) === JSON.stringify([...currentIds].sort());

            if (!isSame) {
              console.log(`[UserDataContext] Ownership Sync: Updating forum IDs for ${firebaseUser.uid}`, actualIds);
              
              // Also heal the document if it's out of sync
              const userRef = doc(db, 'users', firebaseUser.uid);
              updateDoc(userRef, { ownedForumIds: actualIds }).catch(e => console.warn('Failed to heal ownedForumIds doc:', e));
              
              return { ...prev, ownedForumIds: actualIds };
            }
            return prev;
          });
        });
      } catch (err) {
        console.error('[UserDataContext] Ownership listeners failed:', err);
      }
    };

    setupOwnershipListeners();
    return () => {
      if (unsubTribes) unsubTribes();
      if (unsubForums) unsubForums();
    };
  }, [firebaseUser?.uid, userProfile.id]);

  // Sync interactions (passed/liked users) to prevent ghost reappearances
  useEffect(() => {
    // ASYNC GUARD
    if (DISABLE_ASYNC_EFFECTS) return;

    if (!firebaseUser || !userProfile.id) return;

    const syncInteractions = async () => {
      try {
        const { UserService } = await import('../services/api');
        const swipedIds = await UserService.fetchUserInteractions(String(userProfile.id));
        if (swipedIds.length > 0) {
          setPassedUserIds(new Set(swipedIds));
          // [Removed log: was logging swiped interaction count]
        }
      } catch (error) {
        console.error('[UserDataContext] Failed to sync interactions:', error);
      }
    };
    syncInteractions();
  }, [firebaseUser, userProfile.id]);

  const updateProfile = async (updates: Partial<UserProfile>) => {
    // Prevent concurrent updates
    if (isUpdating) {
      console.warn('Update already in progress, skipping');
      return;
    }

    if (!firebaseUser || firebaseUser.uid === 'mock-user-id') {
      // If not logged in or mock user, just update local state
      setUserProfile((prev) => ({ ...prev, ...updates }));
      console.log('Local profile updated (mock/offline):', updates);
      return;
    }

    // Persist blockedUsers if present in updates (though we handle it separately in blockUser)


    // 🔒 PATCH 1D & 4C: Enforce cooldown and claim username atomically
    // Trigger if username changed OR if it's the first time (no lastUsernameChange)
    if (updates.username && (updates.username !== userProfile.username || !userProfile.lastUsernameChange)) {
      // Validate username format
      const usernameWithoutAt = updates.username.replace('@', '');
      const validFormat = /^[a-zA-Z0-9_.]+$/.test(usernameWithoutAt);

      if (!validFormat) {
        throw new Error('Invalid username format. Use only letters, numbers, underscores, and periods.');
      }

      if (usernameWithoutAt.length < 4) {
        throw new Error('Username must be at least 4 characters.');
      }

      // Check cooldown
      if (!canChangeUsername()) {
        throw new Error('You can only change your username once every 30 days.');
      }

      // 🔒 PATCH 6A: Recheck availability before claiming
      const isAvailable = await UsernameService.checkUsernameAvailability(usernameWithoutAt);
      if (!isAvailable) {
        throw new Error('Username is already taken');
      }

      // 🔒 PATCH 4C & 7A: Claim username atomically (releases old username)
      const oldUsername = userProfile.username ? userProfile.username.replace('@', '') : null;
      const claimResult = await UsernameService.claimUsername(
        usernameWithoutAt,
        oldUsername,
        firebaseUser.uid,
        firebaseUser.email ?? undefined
      );

      if (!claimResult.success) {
        throw new Error(claimResult.error || 'Failed to claim username');
      }

      // Automatically set lastUsernameChange to server timestamp
      updates.lastUsernameChange = serverTimestamp();
    }

    // 🔒 SECURITY: Sanitize updates to prevent unauthorized rank/gold mutation
    // Rank and Gold are authoritative fields and must only be modified by server/admin
    if ('rank' in updates) {
      console.warn('SECURITY: Attempted to update rank from client. Request blocked.');
      delete updates.rank;
    }
    if ('gold' in updates) {
      console.warn('SECURITY: Attempted to update gold from client. Request blocked.');
      delete updates.gold;
    }

    // 🔒 Cloud-Function-only fields (see isAdminFieldUpdate in firestore.rules).
    //
    // The verification flow legitimately learns these from a CF response and has
    // to show them immediately, but including them in the CLIENT's own write
    // makes the entire setDoc fail with permission-denied. That is why a
    // successful email or phone verification ended in "Verification failed":
    // the code was accepted, the CF wrote the fields, and then this write threw
    // on the way back and the error surfaced as the verification failing.
    //
    // Held aside for local state, dropped from the payload.
    const cfOnlyFields: Partial<UserProfile> = {};
    for (const key of ['emailVerified', 'phoneVerified', 'isRelayEmail', 'verifiedBadge'] as const) {
      if (key in updates) {
        (cfOnlyFields as any)[key] = (updates as any)[key];
        delete (updates as any)[key];
      }
    }

    setIsUpdating(true);
    try {
      const db = await getDbSafe();
      const userRef = doc(db, 'users', firebaseUser.uid);
      
      // Capture before-states for cascading updates
      const oldName = userProfile.name;
      const oldUsername = userProfile.username;
      const oldAvatar = userProfile.avatar;

      // Automatically update the 'avatar' field if portfolio or index changed
      if (updates.portfolio || updates.profilePictureIndex !== undefined) {
        const port = updates.portfolio || userProfile.portfolio;
        const idx = updates.profilePictureIndex !== undefined 
          ? updates.profilePictureIndex 
          : (userProfile.profilePictureIndex || 0);
        
        if (port && port[idx]) {
          updates.avatar = port[idx];
        }

        // 🔒 PATCH: Ensure portfolioPreviews stays in sync in length with portfolio
        // If portfolio is updated but previews isn't explicitly provided, pad the array
        if (updates.portfolio && !updates.portfolioPreviews) {
             const currentPreviews = userProfile.portfolioPreviews || [];
             updates.portfolioPreviews = updates.portfolio.map((_, i) => currentPreviews[i] || '');
        }
      }

      // 🔒 CONSISTENCY: Keep bannerImage and coverImage in sync
      if (updates.bannerImage && !updates.coverImage) {
        updates.coverImage = updates.bannerImage;
      } else if (updates.coverImage && !updates.bannerImage) {
        updates.bannerImage = updates.coverImage;
      }

      // Stamp tagsUpdatedAt whenever tags change so TagAffinityService can
      // invalidate cached affinity docs via timestamp comparison.
      if ('identities' in updates || 'interests' in updates) {
        (updates as any).tagsUpdatedAt = serverTimestamp();
      }

      // Use setDoc with merge to safely update without overwriting
      await setDoc(userRef, {
        ...updates,
        lastModified: serverTimestamp(),
      }, { merge: true });

      const nameChanged = updates.name && updates.name !== oldName;
      const userNameChanged = updates.username && updates.username !== oldUsername;
      const avatarChanged = updates.avatar && updates.avatar !== oldAvatar;
      const countryFlagChanged = updates.countryFlag && updates.countryFlag !== userProfile.countryFlag;
      const bannerChanged = updates.bannerImage && updates.bannerImage !== userProfile.bannerImage;
      const showLocationChanged = updates.showLocation !== undefined && updates.showLocation !== userProfile.showLocation;
      const residenceChanged = updates.home_residence && updates.home_residence !== userProfile.home_residence;

      if (nameChanged || userNameChanged || avatarChanged || countryFlagChanged || bannerChanged || showLocationChanged || residenceChanged) {
        // Fire and forget cascading sync to avoid blocking UI thread
        const { AdminService } = await import('../services/api');
        AdminService.syncProfileToPosts(firebaseUser.uid, {
          name: nameChanged ? updates.name : undefined,
          username: userNameChanged ? updates.username : undefined,
          avatar: avatarChanged ? updates.avatar : undefined,
          countryFlag: countryFlagChanged ? updates.countryFlag : undefined,
          bannerImage: bannerChanged ? updates.bannerImage : undefined,
          coverImage: bannerChanged ? updates.bannerImage : undefined,
          showLocation: showLocationChanged ? updates.showLocation : undefined,
          home_residence: residenceChanged ? updates.home_residence : undefined,
        });
      }

      // 🔒 PATCH: Optimistically update local state immediately
      // This ensures the UI reflects changes (bio, portfolio, etc.) without waiting for a fetch
      setUserProfile((prev) => ({
        ...prev,
        ...updates,
        // Not written above, but the CF already persisted them — so they are
        // true of the server state and belong on screen now.
        ...cfOnlyFields,
      } as UserProfile));

      console.log('Profile updated successfully');
    } catch (error) {
      console.error("Error updating profile:", error);
      // 🔒 PATCH: Do NOT update local state if DB write fails.
      // This prevents state corruption where UI shows data that isn't in DB.
      // setUserProfile((prev) => ({ ...prev, ...updates })); <--- REMOVED
      throw error; // Re-throw to let caller handle
    } finally {
      setIsUpdating(false);
    }
  };

  const canChangeUsername = () => {
    // 🔒 PATCH 1A: Legacy users without timestamp get one free rename
    if (!userProfile.lastUsernameChange) return true;

    // 🔒 PATCH 1B & 1C: Handle Firestore Timestamp with malformed protection
    try {
      let timestampMs: number;

      // Handle both Firestore Timestamp and JS Date
      if (typeof userProfile.lastUsernameChange === 'object' && 'toMillis' in userProfile.lastUsernameChange) {
        // Firestore Timestamp
        timestampMs = userProfile.lastUsernameChange.toMillis();
      } else if (userProfile.lastUsernameChange instanceof Date) {
        // JS Date (legacy)
        timestampMs = userProfile.lastUsernameChange.getTime();
      } else if (typeof userProfile.lastUsernameChange === 'number') {
        // Unix timestamp
        timestampMs = userProfile.lastUsernameChange;
      } else {
        // Unknown format - log warning and allow rename (fail-safe)
        console.warn('Unknown lastUsernameChange format:', userProfile.lastUsernameChange);
        return true;
      }

      // 🔒 PATCH 1A: Calculate days since change (30 days, not 7)
      const daysSinceChange = (Date.now() - timestampMs) / (1000 * 60 * 60 * 24);
      return daysSinceChange >= 30;
    } catch (error) {
      // 🔒 PATCH 1C: Malformed timestamp - log and allow rename (fail-safe)
      console.error('Error checking username cooldown:', error);
      return true;
    }
  };

  const addShout = (yap: Yap, caption?: string, images?: string[], audioData?: { url: string, duration: number }) => {
    // 🔒 SECURITY: Banned users cannot shout (client-side guard; Firestore rules enforce backend)
    if ((userProfile as any).status === 'banned') {
      toast.error('Your account has been suspended. You cannot reshare posts.');
      return;
    }

    const newShout: ShoutedYap = {
      id: `${yap.id}-${Date.now()}`,
      originalYap: {
        ...yap,
        // Ensure these fields exist for consistent access
        // Handle both formats: yap.author OR yap.userName
        userName: (yap as any).userName || yap.author,
        author: yap.author || (yap as any).userName,
        userUsername: (yap as any).userUsername || yap.username,
        username: yap.username || (yap as any).userUsername,
        userAvatar: (yap as any).userAvatar || yap.avatar,
        avatar: yap.avatar || (yap as any).userAvatar,
        userCountry: (yap as any).userCountry || yap.country,
        timestamp: (yap as any).timestamp || yap.time,
        time: yap.time || (yap as any).timestamp,
        friendsOnly: yap.visibility === 'friends',
      } as any,
      shoutedBy: (userProfile as any).id || userProfile.username,
      shoutedByUsername: userProfile.username,
      shoutedByName: userProfile.name,
      shoutedByAvatar: userProfile.avatar,
      shoutedByRank: userProfile.rank,
      shoutedByCountryFlag: getUserOriginFlag(userProfile),
      shoutedByLocation: getUserLocationText(userProfile),
      shoutedByResidence: userProfile.home_residence || '',
      shoutedByBannerImage: userProfile.bannerImage,
      shoutedByCoverImage: userProfile.coverImage || userProfile.bannerImage,
      shoutedByShowLocation: userProfile.showLocation ?? true,
      timestamp: new Date().toISOString(),
      shoutCaption: caption,
      shoutImages: images,
      audioUrl: audioData?.url,
      duration: audioData?.duration,
      likes: 0,
      comments: 0,
    };
    setShoutedYaps((prev) => [...prev, newShout]);
  };

  const removeShoutLocal = (yapId: string) => {
    setShoutedYaps((prev) => prev.filter((shout) =>
      shout.id !== yapId && shout.originalYap?.id !== yapId
    ));
  };

  const removeShout = async (yapId: string) => {
    // Optimistically remove by EITHER shout doc ID OR original yap ID
    // (different call sites pass different IDs)
    let removed: ShoutedYap[] = [];
    setShoutedYaps((prev) => {
      removed = prev.filter((shout) => shout.id === yapId || shout.originalYap?.id === yapId);
      return prev.filter((shout) => shout.id !== yapId && shout.originalYap?.id !== yapId);
    });
    // Persist to backend
    try {
      const { YapService } = await import('../services/api');
      await YapService.unshoutYap(yapId);
      window.dispatchEvent(new CustomEvent('yapUpdated'));
    } catch (error) {
      console.error('[UserDataContext] Error deleting shout from backend:', error);
      // Put it back — the shout still exists server-side, so leaving it hidden
      // would desync until the next reload.
      if (removed.length) setShoutedYaps((prev) => [...prev, ...removed]);
      toast.error("Couldn't delete that shout. Try again.");
    }
  };

  const updateShoutCaption = async (yapId: string, newCaption?: string, newImages?: string[]) => {
    // Optimistically update local state
    setShoutedYaps((prev) =>
      prev.map((shout) =>
        shout.originalYap.id === yapId ? { ...shout, shoutCaption: newCaption, shoutImages: newImages } : shout
      )
    );
    // Persist to backend
    try {
      const { YapService } = await import('../services/api');
      await YapService.updateShoutCaption(yapId, newCaption, newImages);
    } catch (error) {
      console.error('[UserDataContext] Error updating shout caption in backend:', error);
    }
  };

  const isYapShouted = (yapId: string) => {
    return shoutedYaps.some((shout) => shout.originalYap.id === yapId);
  };

  const addFriend = async (friendId: string) => {
    if (firebaseUser) {
      try {
        // Use UserService to add to subcollection (transactional)
        const { UserService } = await import('../services/api');
        await UserService.addFriend(firebaseUser.uid, friendId);
      } catch (error) {
        console.error("Error adding friend:", error);
      }
    } else {
      // Local mock for non-logged in (shouldn't happen in real usage)
      console.warn("Cannot add friend while not logged in");
    }
  };

  const removeFriend = async (friendId: string) => {
    if (firebaseUser) {
      try {
        const { UserService } = await import('../services/api');
        await UserService.removeFriend(firebaseUser.uid, friendId);
      } catch (error) {
        console.error("Error removing friend:", error);
      }
    } else {
      setFriends((prev) => prev.filter((friend) => String(friend.id) !== friendId));
    }
  };

  const isFriend = (userId: string) => {
    return friends.some(f => String(f.id) === String(userId)) || optimisticFriends.includes(String(userId));
  };

  const getFriendsCount = () => {
    // 🔒 Filter out ghost/incomplete accounts from the base count
    const filteredFriends = friends.filter((f: any) => 
      f.name && f.name.trim() !== '' && f.name !== 'User' && f.setupComplete !== false
    );
    return getOptimisticCount(filteredFriends.length, String(userProfile.id), 'user', 'friendsCount');
  };

  const blockUser = async (userId: string) => {
    // Every call site wraps its id in String(), so a field that is missing
    // arrives here as the literal "undefined" rather than as undefined.
    // Firestore stores that string without complaint, producing a block that
    // can never match an account while the UI reports success — the user
    // believes they blocked someone and keeps seeing them. One account in
    // production carries exactly such an entry.
    //
    // Refusing here rather than only at the call sites: this is the single
    // choke point, and a silent no-op is the worst possible outcome for a
    // safety feature. Better to fail visibly and let them try again.
    const targetId = String(userId ?? '').trim();
    if (!targetId || targetId === 'undefined' || targetId === 'null') {
      console.error('[blockUser] refused: invalid user id', userId);
      toast.error("Couldn't block this user. Try again from their profile.");
      return;
    }
    if (firebaseUser?.uid && targetId === firebaseUser.uid) {
      console.error('[blockUser] refused: self-block');
      return;
    }

    setBlockedUsers((prev) => {
      if (prev.includes(targetId)) return prev;
      return [...prev, targetId];
    });

    // Persist to Firestore
    if (firebaseUser && firebaseUser.uid !== 'mock-user-id') {
      try {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', firebaseUser.uid);

        // 🔒 FRIEND REMOVAL: Always remove from friend subcollection on both sides
        // before blocking so they no longer appear in the friend list.
        const isFriendAlready = friends.some(f => String(f.id) === targetId);
        if (isFriendAlready) {
          console.log(`[blockUser] Removing ${targetId} from friends before blocking.`);
          const batch = writeBatch(db);
          batch.delete(doc(db, 'users', firebaseUser.uid, 'friends', targetId));
          batch.delete(doc(db, 'users', targetId, 'friends', firebaseUser.uid));
          await batch.commit().catch(err => console.warn('[blockUser] Friend removal batch failed:', err));
        }

        await updateDoc(userRef, {
          blockedUsers: arrayUnion(targetId)
        });
        toast.success('User blocked successfully');

        // Blocking no longer files a moderation report.
        //
        // Every block used to submit one automatically, so the Watchers queue
        // received a report for each block regardless of cause. Blocking is
        // mostly "I would rather not see this person" -- an ex, an argument, a
        // topic someone is tired of -- and treating all of it as an abuse signal
        // buried the reports someone actually chose to file, which are the ones
        // that need a human.
        //
        // It also quietly accused the blocked user: a report is a moderation
        // record against an account, created here with no claim of wrongdoing
        // and no way for the blocker to know it happened.
        //
        // Apple Guideline 1.2 asks for a reporting mechanism, not that blocking
        // be one. The explicit Report flow on profiles, yaps and shouts still
        // covers it.
      } catch (error) {
        console.error("Error blocking user:", error);
        toast.error("Failed to block user");
        // Revert local state
        setBlockedUsers((prev) => prev.filter(id => id !== targetId));
      }
    }
  };

  const unblockUser = async (userId: string) => {
    setBlockedUsers((prev) => prev.filter((user) => user !== userId));

    // Persist to Firestore
    if (firebaseUser && firebaseUser.uid !== 'mock-user-id') {
      try {
        const db = await getDbSafe();
        const userRef = doc(db, 'users', firebaseUser.uid);
        await updateDoc(userRef, {
          blockedUsers: arrayRemove(userId)
        });
      } catch (error) {
        console.error("Error unblocking user:", error);
        toast.error("Failed to unblock user");
        // Revert local state
        setBlockedUsers((prev) => [...prev, userId]);
      }
    }
  };

  const isUserBlocked = (userId: string) => {
    return blockedUsers.includes(userId);
  };

  // ── Muted words ────────────────────────────────────────────────────────────
  // Same shape as blockUser/unblockUser: optimistic local update, arrayUnion /
  // arrayRemove on the server, revert local state if the write fails. The prefs
  // document may not exist yet, so writes go through setDoc(..., { merge: true })
  // rather than updateDoc.
  const muteWord = async (word: string) => {
    const normalized = normalizeMutedWord(word);
    if (!normalized) return;
    if (mutedWords.map(normalizeMutedWord).includes(normalized)) return;
    if (mutedWords.length >= MAX_MUTED_WORDS) {
      toast.error(`You can mute up to ${MAX_MUTED_WORDS} words.`);
      return;
    }

    setMutedWords(prev => [...prev, normalized]);

    if (!firebaseUser || firebaseUser.uid === 'mock-user-id') return;
    try {
      const db = await getDbSafe();
      await setDoc(
        doc(db, 'users', firebaseUser.uid, 'settings', 'preferences'),
        { mutedWords: arrayUnion(normalized), mutedWordsUpdatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (error) {
      console.error('Error muting word:', error);
      toast.error('Failed to mute word');
      setMutedWords(prev => prev.filter(w => w !== normalized));
    }
  };

  const unmuteWord = async (word: string) => {
    const normalized = normalizeMutedWord(word);
    if (!normalized) return;

    const previous = mutedWords;
    setMutedWords(prev => prev.filter(w => normalizeMutedWord(w) !== normalized));

    if (!firebaseUser || firebaseUser.uid === 'mock-user-id') return;
    try {
      const db = await getDbSafe();
      // Remove every stored spelling that normalizes to the same word, so a
      // legacy entry saved before normalization can still be cleared.
      const variants = previous.filter(w => normalizeMutedWord(w) === normalized);
      await setDoc(
        doc(db, 'users', firebaseUser.uid, 'settings', 'preferences'),
        { mutedWords: arrayRemove(...variants), mutedWordsUpdatedAt: serverTimestamp() },
        { merge: true },
      );
    } catch (error) {
      console.error('Error unmuting word:', error);
      toast.error('Failed to unmute word');
      setMutedWords(previous);
    }
  };

  const sendFriendRequest = async (toUserId: string, toUserName: string, toUserUsername: string, toUserAvatar: string, toUserAge?: number) => {
    if (!firebaseUser || !userProfile.id) return { isMatch: false };

    // 🔒 CONCURRENCY LOCK: Prevent rapid double-clicks from sending duplicate notifications
    const targetId = String(toUserId);
    if (pendingRequestsRef.current.has(targetId)) {
      console.warn(`[SocialGraph] Friend request to ${targetId} is already in-flight. Ignoring.`);
      return { isMatch: false };
    }
    pendingRequestsRef.current.add(targetId);

    // 🔒 SECURITY: Banned users cannot send friend requests
    if ((userProfile as any).status === 'banned') {
      toast.error('Your account has been suspended. You cannot send friend requests.');
      return { isMatch: false };
    }

    // [Removed PII log: was logging userProfile.id + username + toUserId]

    // 🔒 Persistence: Record 'like' interaction immediately
    const { UserService } = await import('../services/api');
    UserService.recordUserInteraction(String(userProfile.id), toUserId, 'like').catch(() => {});
    
    // 🔒 Persistence: Update LocalStorage for 'like' to prevent reappearance
    setLikedUserIds(prev => {
      const next = new Set([...prev, toUserId]);
      try {
        localStorage.setItem('likedUserIds', JSON.stringify(Array.from(next)));
      } catch (e) {
        console.warn('Failed to save likedUserIds to localStorage', e);
      }
      return next;
    });
    
    setPassedUserIds(prev => new Set([...prev, toUserId]));

    try {
      const db = await getDbSafe();

      // 🔒 System mod account (@watcher "AfroYap Moderation Team") cannot be
      // friended. Server-side trigger enforces this too; this is the UX path.
      try {
        const { doc, getDoc } = await import('firebase/firestore');
        const targetSnap = await getDoc(doc(db, 'users', targetId));
        if (targetSnap.exists() && targetSnap.data()?.isSystemModAccount === true) {
          toast.error('You cannot add this account as a friend');
          pendingRequestsRef.current.delete(targetId);
          return { isMatch: false };
        }
      } catch (e) {
        // Non-fatal — the Cloud Function trigger still rejects it server-side.
      }

      // 🔒 Social Graph Authority 0: Legacy/Redundancy Friendship Check
      // If we are already friends via ANY path, trigger match immediately.
      if (isFriend(toUserId)) {
         console.log(`[SocialGraph] ALREADY friends with ${toUserId}. Triggering match celebration.`);
         return {
           isMatch: true,
           userData: {
             id: toUserId,
             name: toUserName,
             username: toUserUsername,
             image: toUserAvatar,
           }
         };
      }

      // 🔒 Bulletproof Match: Local-First Check
      const localMatch = friendRequests.find(req => String(req.fromUserId) === String(toUserId));
      
      let snapshot: any = { empty: true, docs: [] };
      
      if (localMatch) {
         console.log(`[SocialGraph] LOCAL match detected for ${toUserId}. bypassing firestore query.`);
         // Fetch the real notification document for the local match so we can mark it as accepted
         const docRef = doc(db, 'notifications', localMatch.id);
         const docSnap = await getDoc(docRef);
         if (docSnap.exists()) {
           snapshot = { empty: false, docs: [docSnap] };
         }
      } else {
        // 🔒 Fallback: Dual-Type Firestore Query
        try {
          const q = query(
            collection(db, 'notifications'),
            where('userId', '==', String(userProfile.id)),
            where('metadata.fromUserId', 'in', [String(toUserId), Number(toUserId)].filter(v => typeof v !== 'number' || !isNaN(v as any)))
          );
          const rawSnap = await getDocs(q);
          const validDocs = rawSnap.docs.filter(d => ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'].includes(d.data().type));
          snapshot = validDocs.length > 0 ? { empty: false, docs: validDocs } : { empty: true, docs: [] };
          
          if (snapshot.empty) {
            // 🔒 Last Resort: Check root senderId field
            const qFallback = query(
              collection(db, 'notifications'),
              where('userId', '==', String(userProfile.id)),
              where('senderId', 'in', [String(toUserId), Number(toUserId)].filter(v => typeof v !== 'number' || !isNaN(v as any)))
            );
            const rawFallbackSnap = await getDocs(qFallback);
            const validFallbackDocs = rawFallbackSnap.docs.filter(d => ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'].includes(d.data().type));
            snapshot = validFallbackDocs.length > 0 ? { empty: false, docs: validFallbackDocs } : { empty: true, docs: [] };
          }
        } catch (checkError) {
          console.warn("[SocialGraph] Match check failed (likely missing index). Proceeding to default flow.", checkError);
        }
      }

      if (!snapshot.empty) {
        console.log(`[SocialGraph] Match detected! Mutual request found from ${toUserId}`);
        const matchNotification = snapshot.docs[0];

        // 🔒 Social Graph Authority 2: Mutual record creation in atomic batch
        const batch = writeBatch(db);

        // My side friend entry
        batch.set(doc(db, 'users', String(userProfile.id), 'friends', toUserId), {
          addedAt: serverTimestamp(),
          status: 'accepted'
        });

        // Their side friend entry
        batch.set(doc(db, 'users', String(toUserId), 'friends', String(userProfile.id)), {
          addedAt: serverTimestamp(),
          status: 'accepted'
        });

        // 3. Update friendsCount for both users (Atomic Source of Truth)
        batch.update(doc(db, "users", String(userProfile.id)), { friendsCount: increment(1) });
        batch.update(doc(db, "users", String(toUserId)), { friendsCount: increment(1) });

        // 🔒 Bilateral Cleanup: Resolve ALL pending requests between these two users
        try {
          // 1. My incoming request from them (The one we just matched)
          batch.update(doc(db, 'notifications', matchNotification.id), {
            read: true,
            status: 'accepted'
          });

          // 2. My outgoing request to them (If any exists)
          const qOutgoing = query(
            collection(db, 'notifications'),
            where('userId', '==', String(toUserId)),
            where('metadata.fromUserId', 'in', [String(userProfile.id), Number(userProfile.id)].filter(v => typeof v !== 'number' || !isNaN(v as any)))
          );
          const outgoingSnap = await getDocs(qOutgoing);
          outgoingSnap.docs.filter(d => ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'].includes(d.data().type)).forEach(d => {
            batch.update(doc(db, 'notifications', d.id), {
              status: 'accepted',
              read: true
            });
          });
        } catch (cleanupErr) {
          console.warn("[SocialGraph] Bilateral cleanup failed:", cleanupErr);
          // Non-fatal, matchNotification update already handled above or will be retried
        }

        // Create 'friend_accepted' notification for THEM
        const newNotifRef = doc(collection(db, 'notifications'));
        batch.set(newNotifRef, {
          userId: toUserId,
          type: 'FRIEND_ACCEPTED',
          title: 'You are now friends!',
          message: `You and ${userProfile.name} are now connected. Start a conversation, slap them or stalk their yaps.`,
          timestamp: serverTimestamp(),
          read: false,
          // Written because NotificationService.sendNotification collapses a
          // FRIEND_ACCEPTED onto an existing unread one from the same sender by
          // reading this top-level field. This document is built by hand rather
          // than through that helper, and without the field it was invisible to
          // that check: the accept path could not see this one and wrote a
          // second notification for the same connection.
          senderId: String(userProfile.id),
          userName: userProfile.name,
          userAvatar: userProfile.avatar,
          userUsername: (userProfile.username || '').replace(/@/g, ''),
          metadata: {
            fromUserId: String(userProfile.id),
            fromUsername: (userProfile.username || '').replace(/@/g, '')
          }
        });

        // 4. Bilateral Cleanup: Mark any existing OUTGOING requests from me to them as accepted
        // This handles cases where both users sent requests at different times.
        try {
          const outgoingQ = query(
            collection(db, 'notifications'),
            where('userId', '==', toUserId),
            where('metadata.fromUserId', '==', userProfile.id),
            where('status', '==', 'pending')
          );
          const outgoingSnap = await getDocs(outgoingQ);
          outgoingSnap.docs.filter(d => ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'].includes(d.data().type)).forEach(docSnap => {
            batch.update(doc(db, 'notifications', docSnap.id), { status: 'accepted', read: true });
          });
        } catch (cleanupErr) {
          console.warn('[SocialGraph] Mutual match cleanup failed:', cleanupErr);
        }

        await batch.commit();
        console.log(`[SocialGraph] Connection committed successfully for ${userProfile.id} <-> ${toUserId}`);
        
        // 🔒 OPTIMISM: 
        // 1. Add to optimistic friends so UI updates immediately
        setOptimisticFriends(prev => [...prev, toUserId]);
        // 2. Remove from outgoing/incoming requests states
        setOutgoingRequests(prev => prev.filter(id => id !== toUserId));
        setFriendRequests(prev => prev.filter(req => req.fromUserId !== toUserId));
        
        // 3. Increment friends count for both locally
        adjustMetric(String(userProfile.id), 'user', 'friendsCount', 1);
        adjustMetric(toUserId, 'user', 'friendsCount', 1);
        
        toast.success(`You are now friends with ${toUserName}!`);

        return {
          isMatch: true,
          userData: {
            id: toUserId,
            name: toUserName,
            username: toUserUsername,
            image: toUserAvatar,
            age: toUserAge
          }
        };
      }

      // 🔒 Social Graph Authority 3: Send real request if no match
      let outgoingSnap: any = { empty: true, docs: [] };
      try {
        const outgoingQ = query(
          collection(db, 'notifications'),
          where('userId', '==', toUserId),
          where('metadata.fromUserId', '==', userProfile.id)
        );
        const rawSnap = await getDocs(outgoingQ);
        const validDocs = rawSnap.docs.filter(d => ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'].includes(d.data().type));
        outgoingSnap = validDocs.length > 0 ? { empty: false, docs: validDocs } : { empty: true, docs: [] };
      } catch (checkError) {
        console.warn("[SocialGraph] Duplicate check failed (likely missing index). Sending fresh request.", checkError);
      }
      
      if (outgoingSnap.empty) {
        // [Removed PII log: was logging target + from @{(yap?.username || 'unknown').replace(/@/g, '').toLowerCase()};
        
        const cleanUsername = (userProfile.username || '').trim().replace(/@/g, '');
        
        await NotificationService.sendNotification(
          toUserId,
          'FRIEND_REQUEST',
          `${userProfile.name} wants to connect`,
          `Tap to view their profile and respond.`,
          {
            fromUserId: String(userProfile.id),
            fromUserName: userProfile.name,
            fromUserAvatar: userProfile.avatar || userProfile.image,
            fromUsername: cleanUsername
          }
        );
        // Immediately update outgoing requests state so button shows "Request Sent"
        setOutgoingRequests(prev => [...new Set([...prev, toUserId])]);
      } else {
        // Pending request already exists — do not send duplicate
        // [Removed PII log: was logging sender + target IDs]
        setOutgoingRequests(prev => [...new Set([...prev, toUserId])]);
      }

      return { isMatch: false };
    } catch (error) {
      console.error("[SocialGraph] Error processing interaction:", error);
      throw error; // Rethrow to allow UI components to handle failure
    } finally {
      pendingRequestsRef.current.delete(targetId);
    }
  };


  const passUser = async (targetUserId: string) => {
    if (!firebaseUser || !userProfile.id) return;
    
    // 🔒 Persistence: Record 'pass' interaction
    // We update local state AND LocalStorage immediately to ensure they don't reappear
    setPassedUserIds(prev => {
      const next = new Set([...prev, targetUserId]);
      try {
        localStorage.setItem('passedUserIds', JSON.stringify(Array.from(next)));
      } catch (e) {
        console.warn('Failed to save passedUserIds to localStorage', e);
      }
      return next;
    });

    try {
      const { UserService } = await import('../services/api');
      // Attempt server write (fire & forget, we have local fallback)
      UserService.recordUserInteraction(String(userProfile.id), targetUserId, 'pass').catch(() => {});
      
      const db = await getDbSafe();
      
      // Check if they had sent me a request
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userProfile.id),
        where('metadata.fromUserId', '==', targetUserId),
        where('read', '==', false)
      );
      
      const rawSnapshot = await getDocs(q);
      const docs = rawSnapshot.docs.filter(d => ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'].includes(d.data().type));
      
      if (docs.length > 0) {
        // [Removed PII log: was logging passedUserId]
        const batch = writeBatch(db);
        
        docs.forEach(docSnap => {
          batch.update(doc(db, 'notifications', docSnap.id), {
            read: true,
            status: 'rejected'
          });
        });
        
        await batch.commit();
      }
    } catch (error) {
      console.error("[SocialGraph] Error passing user:", error);
    }
  };

  const acceptFriendRequest = async (notificationId: string) => {
    if (!firebaseUser) return;

    try {
      const db = await getDbSafe();
      // 1. Get the notification to find the sender
      const notifRef = doc(db, 'notifications', notificationId);
      const notifSnap = await getDoc(notifRef);

      if (!notifSnap.exists()) {
        console.error("[acceptFriendRequest] Notification not found:", notificationId);
        throw new Error("Notification not found");
      }

      const notifData = notifSnap.data();
      if (notifData.status === 'accepted') {
        console.log("[acceptFriendRequest] Request already accepted. Skipping.");
        return;
      }

      // Use the existing notifData
      const senderId = String(notifData.metadata?.fromUserId || notifData.senderId); // Metadata usually has it

      if (!senderId) {
        console.error("[acceptFriendRequest] Sender ID missing in notification metadata:", notifData);
        throw new Error("Sender ID missing");
      }

      // Check if already friends to avoid redundant writes/increments
      if (isFriend(senderId)) {
        console.log(`[acceptFriendRequest] Already friends with ${senderId}. Cleaning up notification.`);
        const db_ = await getDbSafe();
        await updateDoc(notifRef, { read: true, status: 'accepted' });
        return;
      }

      const batch = writeBatch(db);

      // 2. Create mutual friend entries
      // My side
      const myFriendRef = doc(db, 'users', String(userProfile.id), 'friends', senderId);
      batch.set(myFriendRef, {
        addedAt: serverTimestamp(),
        status: 'accepted'
      });

      // Their side
      const theirFriendRef = doc(db, 'users', senderId, 'friends', String(userProfile.id));
      batch.set(theirFriendRef, {
        addedAt: serverTimestamp(),
        status: 'accepted'
      });

      // 3. Mark notification as read (or handled)
      batch.update(notifRef, { read: true, status: 'accepted' });

      // 4. Update friendsCount for both users (Atomic Source of Truth)
      batch.update(doc(db, "users", String(userProfile.id)), { friendsCount: increment(1) });
      batch.update(doc(db, "users", senderId), { friendsCount: increment(1) });

      await batch.commit();

      // 5. Tell the sender their request was accepted.
      //
      // Runs AFTER the commit, deliberately. It used to be awaited before it,
      // so a failed notification write threw past the commit and the friendship
      // was never created at all. The notification is the least important part
      // of this function and must not be able to block the rest of it.
      //
      // The copy matches the mutual-match branch of sendFriendRequest word for
      // word: both paths mean the same thing to the person reading it, and the
      // two of them firing for one connection is what produced a "You are now
      // friends!" and a "New Connection" side by side.
      try {
        await NotificationService.sendNotification(
          senderId,
          'FRIEND_ACCEPTED',
          'You are now friends!',
          `You and ${userProfile.name} are now connected. Start a conversation, slap them or stalk their yaps.`,
          {
            fromUserId: userProfile.id,
            fromUserName: userProfile.name,
            fromUserAvatar: userProfile.avatar,
            fromUsername: (userProfile.username || '').replace(/@/g, '')
          }
        );
      } catch (notifErr) {
        console.warn('[acceptFriendRequest] Notification failed (friendship already committed):', notifErr);
      }

      // 🔒 OPTIMISM: 
      // 1. Add to optimistic friends
      setOptimisticFriends(prev => [...prev, senderId]);
      // 2. Remove from pending lists
      setFriendRequests(prev => prev.filter(req => req.fromUserId !== senderId));
      setOutgoingRequests(prev => prev.filter(id => id !== senderId));

      // 3. Increment friends count for both locally
      adjustMetric(String(userProfile.id), 'user', 'friendsCount', 1);
      adjustMetric(senderId, 'user', 'friendsCount', 1);

      // Clean up sender's delta immediately since we're done; 
      // our own will be cleaned up by the onSnapshot above.
      resetMetric(senderId, 'user', 'friendsCount');

      // ✅ Update local friends state immediately so the count reflects right away
      try {
        const senderDoc = await getDoc(doc(db, 'users', senderId));
        if (senderDoc.exists()) {
          const senderData = senderDoc.data() as any;
          const newFriend = {
            id: senderId,
            name: senderData.name || senderData.displayName || 'User',
            username: senderData.username || '',
            image: senderData.avatar || senderData.image || '',
            avatar: senderData.avatar || '',
            age: calculateAge(senderData.dateOfBirth) ?? undefined,
            location: senderData.location || senderData.home_residence || '',
            country: senderData.country || '',
            countryFlag: senderData.countryFlag || '',
          } as any;
          setFriends(prev => {
            // Avoid duplicates
            if (prev.some(f => String(f.id) === senderId)) return prev;
            return [...prev, newFriend];
          });

          // 🎉 Trigger Celebration Popup for manual acceptance
          triggerHaptic('success'); // 📳 iOS parity: haptic on friend request accept
          window.dispatchEvent(new CustomEvent(EVENTS.SHOW_MATCH_POPUP, {
            detail: {
              ...newFriend,
              image: newFriend.avatar || newFriend.image || ''
            }
          }));
        }
      } catch (friendSyncErr) {
        console.warn('[acceptFriendRequest] Could not sync friend to local state:', friendSyncErr);
      }

    } catch (error) {
      console.error("[UserDataContext] Error accepting friend request:", error);
      toast.error("Failed to accept friend request");
      throw error; // Rethrow so components like ProfileScreen know it failed
    }
  };

  const rejectFriendRequest = async (notificationId: string) => {
    if (!firebaseUser) return;

    try {
      const db = await getDbSafe();
      const notifRef = doc(db, 'notifications', notificationId);
      await updateDoc(notifRef, { read: true, status: 'rejected' });
      toast.info("Friend request declined");
    } catch (error) {
      console.error("[UserDataContext] Error rejecting friend request:", error);
      toast.error("Failed to reject request");
      throw error;
    }
  };

  // Take back an outgoing friend request we sent (e.g. tapped "Add Friend" by
  // accident). The request lives as a notification on the *recipient's* side;
  // Firestore rules let the sender (senderId) update but not delete it, so we
  // mark it 'rejected' — the same status the recipient's incoming/declined views
  // already filter out, and which our own outgoing listener treats as resolved.
  /**
   * Take back an outgoing friend request, and give the gold back.
   *
   * The request lives as a notification on the RECIPIENT's side. Firestore rules
   * let the sender update it but not delete it, so it is marked 'rejected' — the
   * status the recipient's incoming view already filters out and our own
   * outgoing listener treats as resolved.
   *
   * `refundAmount` is passed in by the caller because the price is not fixed:
   * a similar user costs SWIPE_COSTS.matching and everyone else
   * FRIEND_REQUEST_COST, and only the screen that charged knows which it paid.
   *
   * THE REFUND IS GATED ON ACTUALLY CANCELLING SOMETHING. The filter below now
   * excludes 'rejected' as well as 'accepted', so a request that is already
   * cancelled matches nothing, no write happens and no gold is paid out.
   * Without that, calling this twice would mint gold each time — the previous
   * filter only excluded 'accepted', so an already-rejected request still
   * counted as cancellable. Gold mutation is client-side throughout this
   * codebase, so the call site is the only place that guard can live.
   */
  const cancelFriendRequest = async (toUserId: string, refundAmount?: number) => {
    if (!firebaseUser || !userProfile.id) return;
    const tid = String(toUserId);

    // Optimistic: drop it from outgoing immediately so the button flips back.
    setOutgoingRequests(prev => prev.filter(id => id !== tid));

    try {
      const db = await getDbSafe();
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', tid),
        where('metadata.fromUserId', '==', userProfile.id)
      );
      const snap = await getDocs(q);
      const docs = snap.docs.filter(d => {
        const data = d.data();
        const status = String(data.status || '');
        return ['FRIEND_REQUEST', 'friend_request', 'CONNECTION_REQUEST', 'connection_request'].includes(data.type)
          && status !== 'accepted'
          && status !== 'rejected';
      });

      if (docs.length === 0) {
        // Nothing pending. The button is already back to idle from the
        // optimistic update, so this is a no-op rather than an error.
        return;
      }

      const batch = writeBatch(db);
      docs.forEach(docSnap => {
        batch.update(doc(db, 'notifications', docSnap.id), { read: true, status: 'rejected' });
      });
      await batch.commit();

      // Only after the cancel has actually committed.
      if (refundAmount && refundAmount > 0) {
        const { EconomyService } = await import('../services/api');
        const econ = await EconomyService.refundGold(
          String(userProfile.id), refundAmount, 'friend_request_refund',
        );
        if (econ.success && econ.newBalance !== undefined) {
          // Discreet on purpose: the balance in the header updates itself, so
          // announcing the amount turns an undo into a reward. One quiet line
          // confirming the cancel is all this needs.
          updateProfile({ gold: econ.newBalance, goldBalance: econ.newBalance });
          toast.info('Request canceled');
          return;
        }
        // The request IS cancelled; only the refund failed. Worth saying, since
        // the balance will not move and silence would look like a bug.
        toast.info('Request canceled. Gold could not be returned.');
        return;
      }

      toast.info('Request canceled');
    } catch (error) {
      console.error('[SocialGraph] Error canceling friend request:', error);
      toast.error('Failed to cancel request');
      // Revert optimistic update so the UI reflects reality.
      setOutgoingRequests(prev => [...new Set([...prev, tid])]);
      throw error;
    }
  };

  const hasPendingRequestTo = (userId: string) => {
    const id = String(userId);
    if (optimisticFriends.includes(id)) return false;
    return outgoingRequests.includes(id);
  };

  const hasPendingRequestFrom = (userId: string) => {
    return friendRequests.some((request) => String(request.fromUserId) === String(userId) && request.status === 'pending');
  };

  const acceptGroupInvite = async (entityType: 'forum' | 'tribe', entityId: string) => {
    if (!firebaseUser) return;
    
    const isForum = entityType === 'forum';

    try {
      const result = await GovernanceService.acceptGroupInvite(entityType, entityId, firebaseUser.uid);
      
      // 🔔 NOTIFICATION: Notify owner
      if (result && result.ownerId && result.ownerId !== firebaseUser.uid) {
        try {
          await NotificationService.sendNotification(
            result.ownerId,
            isForum ? 'FORUM_JOIN' : 'TRIBE_JOIN',
            `New ${isForum ? 'Forum' : 'Tribe'} Member!`,
            `@${(userProfile.username || '').replace(/@/g, '')} has joined your ${isForum ? 'forum' : 'tribe'} "${result.entityName || 'Group'}".`,
            { 
              [isForum ? 'forumId' : 'tribeId']: entityId, 
              joinerId: firebaseUser.uid,
              [isForum ? 'forumName' : 'tribeName']: result.entityName,
              fromUsername: (userProfile.username || '').replace(/@/g, '')
            }
          );
        } catch (nError) {
          console.warn(`[UserDataContext] Failed to send ${entityType} join notification:`, nError);
        }
      }
      
      const now = new Date().toISOString();
      if (isForum) {
        setUserProfile(prev => ({
          ...prev,
          joinedForumIds: [...new Set([...(prev.joinedForumIds || []), entityId])],
          forumJoinTimestamps: { ...(prev.forumJoinTimestamps || {}), [entityId]: now }
        }));
        adjustMetric(entityId, 'forum', 'memberCount', 1);
      } else {
        setUserProfile(prev => ({
          ...prev,
          joinedTribeIds: [...new Set([...(prev.joinedTribeIds || []), entityId])],
          tribeJoinTimestamps: { ...(prev.tribeJoinTimestamps || {}), [entityId]: now }
        }));
        adjustMetric(entityId, 'tribe', 'memberCount', 1);
      }
      
      toast.success(`Joined ${isForum ? 'forum' : 'tribe'}!`);
    } catch (e: any) {
      console.error(`Failed to join ${entityType}:`, e);
      toast.error(e.message || `Failed to join ${entityType}`);
      throw e;
    }
  };

  const joinTribe = async (tribeId: string) => {
    if (!firebaseUser) return;
    
    try {
      const result = await GovernanceService.joinTribe(firebaseUser.uid, tribeId);
      
      // 🔔 NOTIFICATION: Notify tribe owner
      if (result && result.ownerId && result.ownerId !== firebaseUser.uid) {
        try {
          await NotificationService.sendNotification(
            result.ownerId,
            'TRIBE_JOIN',
            'New Tribe Member!',
            `@${(userProfile.username || '').replace(/@/g, '')} has joined your tribe "${result.tribeName || 'Your Tribe'}".`,
            { 
              tribeId, 
              joinerId: firebaseUser.uid,
              tribeName: result.tribeName,
              fromUsername: (userProfile.username || '').replace(/@/g, '')
            }
          );
        } catch (nError) {
          console.warn("[UserDataContext] Failed to send tribe join notification:", nError);
        }
      }
      
      const now = new Date().toISOString();
      setUserProfile(prev => ({
        ...prev,
        joinedTribeIds: [...new Set([...(prev.joinedTribeIds || []), tribeId])],
        tribeJoinTimestamps: { ...(prev.tribeJoinTimestamps || {}), [tribeId]: now }
      }));
      
      // 🔒 OPTIMISM: Increment tribe member count locally
      adjustMetric(tribeId, 'tribe', 'memberCount', 1);
      // Note: We don't reset immediately because tribes/forums don't have real-time listeners at this level.
      // They will clear after the 60s timeout or on manual refresh.
      
      toast.success("Joined tribe!");
    } catch (e: any) {
      console.error("Failed to join tribe:", e);
      toast.error(e.message || "Failed to join tribe");
      throw e; // 🚨 RE-THROW so caller can react
    }
  };

  const leaveTribe = async (tribeId: string) => {
    if (!firebaseUser) return;

    // No minimum-stay lock: members can leave whenever they want, and rejoin
    // immediately. (The old 24h hold also returned silently, so the caller's
    // optimistic "left" UI ran even though the leave never happened.)
    try {
      await GovernanceService.leaveTribe(firebaseUser.uid, tribeId);
      setUserProfile(prev => {
        const newTimestamps = { ...(prev.tribeJoinTimestamps || {}) };
        delete newTimestamps[tribeId];
        return {
          ...prev,
          joinedTribeIds: (prev.joinedTribeIds || []).filter(id => id !== tribeId),
          tribeJoinTimestamps: newTimestamps
        };
      });
      
      // 🔒 OPTIMISM: Decrement tribe member count locally
      adjustMetric(tribeId, 'tribe', 'memberCount', -1);
      
      toast.success("Left tribe!");
    } catch (e: any) {
      console.error("Failed to leave tribe:", e);
      toast.error(e.message || "Failed to leave tribe");
      throw e;
    }
  };

  const isTribeMember = useCallback((tribeId: string, ownerId?: string) => {
    // 🛡️ DIRECT CHECK: If userProfile.id is missing (slow sync), use firebaseUser.uid
    const currentUserId = userProfile.id || firebaseUser?.uid;
    if (!currentUserId) return false;

    const isMemberInProfile = userProfile.joinedTribeIds?.includes(tribeId);
    
    // Check against current ID AND originalId (if any)
    const isOwnerByCurrentId = ownerId !== undefined && String(currentUserId).trim() === String(ownerId).trim();
    const isOwnerByOriginalId = ownerId !== undefined && userProfile.originalId !== undefined && String(userProfile.originalId).trim() === String(ownerId).trim();
    
    const isOwner = isOwnerByCurrentId || isOwnerByOriginalId;

    // [Removed debug log: was logging owner detection with user IDs]

    const result = !!(isMemberInProfile || isOwner);
    // [Removed debug log: was logging isTribeMember result with tribeId]
    
    return result;
  }, [userProfile.joinedTribeIds, userProfile.id, userProfile.originalId, firebaseUser?.uid]);

  const joinForum = async (forumId: string) => {
    if (!firebaseUser) return;

    try {
      const result = await GovernanceService.joinForum(firebaseUser.uid, forumId);
      
      // 🔔 NOTIFICATION: Notify forum owner
      if (result && result.ownerId && result.ownerId !== firebaseUser.uid) {
        try {
          await NotificationService.sendNotification(
            result.ownerId,
            'FORUM_JOIN',
            'New Forum Member!',
            `@${(userProfile.username || '').replace(/@/g, '')} has joined your forum "${result.forumName || 'Your Forum'}".`,
            { 
              forumId, 
              joinerId: firebaseUser.uid,
              forumName: result.forumName,
              fromUsername: (userProfile.username || '').replace(/@/g, '')
            }
          );
        } catch (nError) {
          console.warn("[UserDataContext] Failed to send forum join notification:", nError);
        }
      }
      
      const now = new Date().toISOString();
      setUserProfile(prev => ({
        ...prev,
        joinedForumIds: [...new Set([...(prev.joinedForumIds || []), forumId])],
        forumJoinTimestamps: { ...(prev.forumJoinTimestamps || {}), [forumId]: now }
      }));
      
      // 🔒 OPTIMISM: Increment forum member count locally
      adjustMetric(forumId, 'forum', 'memberCount', 1);
      
      toast.success("Joined forum!");
    } catch (e: any) {
      console.error("Failed to join forum:", e);
      toast.error(e.message || "Failed to join forum");
      throw e; // 🚨 RE-THROW so caller can react
    }
  };

  const leaveForum = async (forumId: string) => {
    if (!firebaseUser) return;

    // No minimum-stay lock — see leaveTribe.
    try {
      await GovernanceService.leaveForum(firebaseUser.uid, forumId);
      setUserProfile(prev => {
        const newTimestamps = { ...(prev.forumJoinTimestamps || {}) };
        delete newTimestamps[forumId];
        return {
          ...prev,
          joinedForumIds: (prev.joinedForumIds || []).filter(id => id !== forumId),
          forumJoinTimestamps: newTimestamps
        };
      });
      
      // 🔒 OPTIMISM: Decrement forum member count locally
      adjustMetric(forumId, 'forum', 'memberCount', -1);
      
      toast.success("Left forum!");
    } catch (e: any) {
      console.error("Failed to leave forum:", e);
      toast.error(e.message || "Failed to leave forum");
      throw e;
    }
  };

  const isForumMember = useCallback((forumId: string, ownerId?: string) => {
    // 🛡️ DIRECT CHECK: If userProfile.id is missing (slow sync), use firebaseUser.uid
    const currentUserId = userProfile.id || firebaseUser?.uid;
    if (!currentUserId) {
      console.log('[UserDataContext] isForumMember: NO USER ID SEARCHING FOR forumId:', forumId);
      return false;
    }

    const isMemberInProfile = userProfile.joinedForumIds?.includes(forumId);
    
    // Check against current ID AND originalId (if any)
    const isOwnerByCurrentId = ownerId !== undefined && String(currentUserId).trim() === String(ownerId).trim();
    const isOwnerByOriginalId = ownerId !== undefined && userProfile.originalId !== undefined && String(userProfile.originalId).trim() === String(ownerId).trim();
    
    const isOwner = isOwnerByCurrentId || isOwnerByOriginalId;

    if (isOwner) {
      console.log('[UserDataContext] isForumMember: OWNER DETECTED!', { 
        currentUserId, 
        originalId: userProfile.originalId, 
        ownerId,
        matchType: isOwnerByCurrentId ? 'current' : 'original'
      });
    }

    const result = !!(isMemberInProfile || isOwner);
    console.log('[UserDataContext] isForumMember Result:', { forumId, result, isMemberInProfile, isOwner });
    
    return result;
  }, [userProfile.joinedForumIds, userProfile.id, userProfile.originalId, firebaseUser?.uid]);

  const getAnonymousPostsRemaining = () => {
    // 20 anonymous posts per day for founding 150 testers
    const DAILY_ANONYMOUS_QUOTA = 20;

    if (!userProfile.anonymousPostTimestamps) return DAILY_ANONYMOUS_QUOTA;

    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Filter timestamps that are within the last 24 hours
    const recentPosts = userProfile.anonymousPostTimestamps.filter(timestamp => {
      const postTime = new Date(timestamp);
      return postTime > twentyFourHoursAgo;
    });

    return Math.max(0, DAILY_ANONYMOUS_QUOTA - recentPosts.length);
  };

  const canPostAnonymously = () => {
    return getAnonymousPostsRemaining() > 0;
  };

  const getTimeUntilNextAnonymousPost = () => {
    if (canPostAnonymously()) return null;
    if (!userProfile.anonymousPostTimestamps) return null;

    const now = new Date();
    const twentyFourHoursAgo = new Date(now.getTime() - 24 * 60 * 60 * 1000);

    // Get posts from last 24 hours
    const recentPosts = userProfile.anonymousPostTimestamps
      .filter(timestamp => new Date(timestamp) > twentyFourHoursAgo)
      .sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

    if (recentPosts.length === 0) return null;

    // The oldest of the recent posts determines when a slot frees up
    // We need to wait until 24 hours after that post
    const oldestPostTime = new Date(recentPosts[0]);
    const nextAvailableTime = new Date(oldestPostTime.getTime() + 24 * 60 * 60 * 1000);

    const diffMs = nextAvailableTime.getTime() - now.getTime();
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (diffHrs > 0) {
      return `${diffHrs}h ${diffMins}m`;
    }
    return `${diffMins}m`;
  };

  return (
    <UserDataContext.Provider value={{
      userProfile,
      firebaseUser,
      authLoading,
      profileSettled,
      profileLoaded,
      banState,
      updateProfile,
      canChangeUsername,
      passedUserIds,
      shoutedYaps,
      addShout,
      removeShout,
      removeShoutLocal,
      updateShoutCaption,
      isYapShouted,
      friends,
      addFriend,
      removeFriend,
      isFriend,
      getFriendsCount,
      blockedUsers,
      blockedBy,
      blockUser,
      unblockUser,
      isUserBlocked,
      mutedWords,
      muteWord,
      unmuteWord,
      friendRequests,
      pendingFriendRequests: friendRequests.filter((request) => request.status === 'pending'),
      sendFriendRequest,
      acceptFriendRequest,
      rejectFriendRequest,
      cancelFriendRequest,
      outgoingRequestsLoaded,
      hasPendingRequestTo,
      hasPendingRequestFrom,
      acceptGroupInvite,
      joinTribe,
      leaveTribe,
      isTribeMember,
      joinForum,
      leaveForum,
      isForumMember,
      canPostAnonymously,
      getAnonymousPostsRemaining,
      getTimeUntilNextAnonymousPost,
      // Exposed Wrappers
      claimDailyStreak: async () => {
        if (!firebaseUser) return;
        try {
          await GoldService.claimDailyStreak(firebaseUser.uid);
          toast.success("Streak claimed!");
        } catch (e: any) {
          console.error(e);
          toast.error(e.message || "Failed to claim streak");
        }
      },
      rewardAd: async () => {
        if (!firebaseUser) return;
        try {
          await GoldService.rewardAd(firebaseUser.uid);
          toast.success("Ad reward granted!");
        } catch (e: any) {
          console.error(e);
          toast.error(e.message || "Ad reward failed");
        }
      },
      createForum: async (data: Partial<Forum>) => {
        if (!firebaseUser) throw new Error("Not logged in");

        // Create limit: cap on forums OWNED, not joined (Legion bypasses)
        if (userProfile.rank !== 'legion' && (userProfile.ownedForumIds?.length || 0) >= 5) {
          toast.error("Forum creation limit reached", { description: "You can own up to 5 forums. Delete one to create a new one." });
          throw new Error("Forum creation limit reached");
        }

        const id = await GovernanceService.createForum(firebaseUser.uid, data);
        
        // 🔒 OPTIMISTIC UPDATE: Sync local state immediately for accurate "Owned" counts
        setUserProfile(prev => ({
          ...prev,
          ownedForumIds: [...new Set([...(prev.ownedForumIds || []), id])],
          joinedForumIds: [...new Set([...(prev.joinedForumIds || []), id])],
        }));

        toast.success("Forum created!");
        return id;
      },

      purchaseGoldPack: async (packId: string, amount: number, price: string) => {
        if (!firebaseUser) return false;
        const result = await EconomyService.purchasePack(firebaseUser.uid, packId, amount, price);
        if (result.success) {
          toast.success(`Purchased ${amount} Gold!`);
          // Update local state immediately for responsiveness
          setUserProfile(prev => ({
            ...prev,
            gold: result.newBalance,
            goldBalance: result.newBalance
          }));
          return true;
        } else {
          toast.error(result.error || "Purchase failed");
          return false;
        }
      },
      createTribe: async (data: Partial<Tribe>, paymentMethod?: 'credit' | 'gold') => {
        if (!firebaseUser) throw new Error("Not logged in");

        // Create limit: cap on tribes OWNED, not joined (Legion bypasses)
        if (userProfile.rank !== 'legion' && (userProfile.ownedTribeIds?.length || 0) >= 4) {
          toast.error("Tribe creation limit reached", { description: "You can own up to 4 tribes. Delete one to establish a new one." });
          throw new Error("Tribe creation limit reached");
        }

        const id = await GovernanceService.createTribe(firebaseUser.uid, data, paymentMethod);

        // 🔒 OPTIMISTIC UPDATE: Sync local state immediately for accurate "Owned" counts
        setUserProfile(prev => ({
          ...prev,
          ownedTribeIds: [...new Set([...(prev.ownedTribeIds || []), id])],
          joinedTribeIds: [...new Set([...(prev.joinedTribeIds || []), id])],
        }));

        toast.success("Tribe established!");
        return id;
      },
      passOwnership: async (type: 'forum' | 'tribe', id: string, newOwnerId: string) => {
        if (!firebaseUser) throw new Error("Not logged in");
        await GovernanceService.passOwnership(type, id, firebaseUser.uid, newOwnerId);
        toast.success("Ownership passed successfully.");
      },
      refreshEntitlements: async () => {
        // Placeholder for forcing a re-fetch or entitlement check
        // Currently Firestore listener updates state automatically.
      },
      simulateSubscription: async (tier: 'yapper' | 'elite' | 'none') => {
        if (!firebaseUser) return;
        // Dev wrapper for SubscriptionService
        await SubscriptionService.grantCreationCreditsOnPurchaseOrRenew(firebaseUser.uid, tier);
        toast.success(`Subscription set to ${tier} (Dev)`);
      },

      passUser,
      likedUserIds: Array.from(likedUserIds),
      sentRequestIds: outgoingRequests,
    }}>
      {children}
    </UserDataContext.Provider >
  );
}

export function useUserData() {
  const context = useContext(UserDataContext);
  if (context === undefined) {
    throw new Error('useUserData must be used within a UserDataProvider');
  }
  return context;
}
